<template>
    <v-container :fluid="true" class="max-height">
        <v-row>
            <v-col class="px-5 d-flex justify-space-between" cols="12">
                <h1 class="text-left">Driver Compliance Report</h1>
            </v-col>
        </v-row>
        <v-row>
            <v-col v-if="user.type === 'admin'" cols="12" md="3" sm="6">
                <company-select v-model="formData['company']" :items="companies" density="compact" label="Select Company"
                          @update:model-value="resetForm();"/>
            </v-col>
            <v-col cols="12" md="3" sm="6">
                <v-select v-model="formData['driver']" :items="drivers" clearable
                          density="compact" :item-title="d => `${d.first_name || ''} ${d.last_name || ''}`.trim()" item-value="id" label="Select Driver"
                          @update:model-value="formData['page']=1;fetchTrips()"/>
            </v-col>
            <v-col cols="12" md="3" sm="6">
                <v-text-field v-model="formData['start_date']" density="compact" label="Start Date" type="date"
                              @click:clear="formData['page']=1;fetchTrips()"
                              @update:model-value="formData['page']=1;fetchTrips()"/>
            </v-col>
            <v-col cols="12" md="3" sm="6">
                <v-text-field v-model="formData['end_date']" density="compact" label="End Date" type="date"
                              @click:clear="formData['page']=1;fetchTrips()"
                              @update:model-value="formData['page']=1;fetchTrips()"/>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12">
                <h3 class="text-start">Drivers List <small>(Drove more then 8 hrs in a day)</small></h3>
            </v-col>
            <v-col cols="12">
                <v-table>
                    <thead>
                    <tr>
                        <th v-if="user.type ==='admin'">Driver ID</th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th>Trip Duration</th>
                        <th>Trip Date</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="trip in trips" v-if="trips.length" :key="trip.id" class="text-left">
                        <td v-if="user.type ==='admin'">{{ trip.driver_id }}</td>
                        <td>{{ (trip.driver?.user?.first_name || trip.driver?.first_name || '') + ' ' + (trip.driver?.user?.last_name || trip.driver?.last_name || '') }}</td>
                        <td>{{ trip.driver?.user?.email || trip.driver?.email || '' }}</td>
                        <td>{{ trip.driver?.user?.phone || trip.driver?.phone || '' }}</td>
                        <td>{{ secToHours(trip.trips_duration) }}</td>
                        <td>{{ trip.trip_date }}</td>
                    </tr>
                    <tr v-else>
                        <td v-if="loading" colspan="7">
                            <v-progress-circular color="primary" indeterminate/>
                        </td>
                        <td v-else colspan="7">{{ user.type === 'admin' && !formData.company ? 'Select a company to view compliance data' : 'No record found' }}</td>
                    </tr>
                    </tbody>
                </v-table>
            </v-col>
        </v-row>

        <v-row v-if="totalPages > 1" justify="center">
            <v-col cols="8">
                <v-pagination v-model="formData['page']" :length="totalPages" class="my-4" density="compact"
                              @update:model-value="fetchTrips"/>
            </v-col>
        </v-row>
    </v-container>
</template>

<script setup>
import {onMounted, reactive, ref} from "vue";
import {send} from "@/composables/apiClient";
import {useAuthStore} from "@/stores/authStore";
import {useSnackbarStore} from "@/stores/snackBarStore";
import CompanySelect from "@/components/globals/company-select.vue";

const trips = ref([]);
const {user} = useAuthStore();
const drivers = ref([]);
const companies = ref([]);
const totalPages = ref(1);
const loading = ref(false);
const {showSnackbar} = useSnackbarStore();
const formData = reactive({
    page: 1,
    driver: null,
    end_date: null,
    start_date: null,
    company: ref(user.type !== 'admin' ? user.company.id : null),
})

onMounted(async () => {
    if (user.type === 'admin') {
        fetchCompaniesList();
    } else {
        fetchTrips();
        fetchDriversList();
    }
});

const resetForm = () => {
    formData['page'] = 1;
    formData['driver'] = null;
    formData['end_date'] = null;
    formData['start_date'] = null;
    fetchTrips();
    fetchDriversList();
}
const secToHours = (seconds) => {
    const pad = (num, size) => num.toString().padStart(size, '0');
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secondsLeft = seconds % 60;

    return `${pad(hours, 2)}:${pad(minutes, 2)}:${pad(secondsLeft, 2)}`;
}

const fetchCompaniesList = () => {
    return send('get', `companies/list`)
        .then((response) => {
            companies.value = response.data.data;
        })
        .catch((error) => {
            showSnackbar('Companies List could not be loaded', 'error')
        })
}
const fetchDriversList = () => {
    send('get', `companies/drivers`, {list: true, company: formData['company']})
        .then((response) => {
            drivers.value = response.data.data;
        })
        .catch((error) => {
            showSnackbar('Drivers List could not be loaded', 'error')
        })
}
const fetchTrips = () => {
    if (formData['company']) {
        loading.value = true;
        send('get', `reports/compliance`, formData)
            .then((response) => {
                loading.value = false;
                trips.value = response.data.data.data ?? [];
                totalPages.value = response.data.data.last_page;
                formData['page'] = response.data.data.current_page;
            })
            .catch((error) => {
                console.log(error)
                showSnackbar('Driver compliance report could not be loaded', 'error')
            }).finally(() => loading.value = false);
    }
}

</script>
